import { useState } from 'preact/hooks'
import { Download, Loader2 } from 'lucide-preact'
import type { DownloadResult } from '@/api/client'
import { triggerBlobDownload } from '@/utils/downloadFile'
import { useToast } from '@/hooks/useToast'

interface Props {
  /** Performs the export request, resolving to the CSV blob (or an error). */
  onExport: () => Promise<DownloadResult>
  /** Filename used when the server doesn't supply a Content-Disposition filename. */
  fallbackFilename: string
  className?: string
}

/**
 * Export-to-CSV button (ORB-373). The caller gates rendering on the export permission
 * (`canExport`); this component owns the request lifecycle: it disables while in flight,
 * triggers the browser download on success, and surfaces a toast on failure (including the
 * server's 403 when the permission was revoked between render and click).
 */
export function AnalyticsExportButton({ onExport, fallbackFilename, className = '' }: Props) {
  const { showError } = useToast()
  const [busy, setBusy] = useState(false)

  const handleExport = async () => {
    setBusy(true)
    try {
      const result = await onExport()
      if (result.error || !result.blob) {
        showError('Export failed', result.error || 'Could not export analytics data.')
        return
      }
      triggerBlobDownload(result.blob, result.filename || fallbackFilename)
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={busy}
      title="Export to CSV"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${className}`}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      {busy ? 'Exporting…' : 'Export'}
    </button>
  )
}
